import { createContext, useContext, useSyncExternalStore } from "react";

// The arena's VIEW orientation. The simulation's maps are landscape (a 2:1
// rectangle), which wastes most of an upright phone. There the view turns a
// quarter clockwise instead: the map's left end is at the top of the screen,
// its top edge on the right. Only the picture turns, never the game, so
// players on different screens still share one map; steering is relative to
// the worm, so the controls don't change either.
//
// What turns with the board: trails, heads, the floor and the wall. What must
// stay upright (so it's drawn counter-turned): power-up icons, victory
// animations, name tags, key cards and anything else with text or an "up".
//
// GameLayout decides (useArenaRotated) and provides it; GameCanvas turns its
// layers, the renderers counter-turn their upright sprites, and the DOM
// overlays place themselves through toView().

export interface ArenaView {
  // true: the view is turned 90 degrees clockwise (map left = screen top)
  rotated: boolean;
}

export const ArenaViewContext = createContext<ArenaView>({ rotated: false });

export function useArenaView(): ArenaView {
  return useContext(ArenaViewContext);
}

const portraitQuery = "(orientation: portrait)";
function subscribePortrait(cb: () => void): () => void {
  if (typeof window === "undefined" || !window.matchMedia) return () => {};
  const mql = window.matchMedia(portraitQuery);
  if (mql.addEventListener) {
    mql.addEventListener("change", cb);
    return () => mql.removeEventListener("change", cb);
  }
  mql.addListener(cb);
  return () => mql.removeListener(cb);
}
function portraitSnapshot(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia(portraitQuery).matches;
}

// Turn the view when the screen is upright and the map is clearly landscape.
// Squares and circles never turn (nothing to gain). `enabled` is the compact
// (narrow) layout: a wide desktop window is never turned. Phones are played
// sideways only now (RotateScreen covers an upright phone), so in practice this
// is for upright tablets and narrow, tall browser windows.
export function useArenaRotated(map: any, enabled: boolean): boolean {
  const portrait = useSyncExternalStore(subscribePortrait, portraitSnapshot, () => false);
  const w = (map && map.width) || 1;
  const h = (map && map.height) || 1;
  return enabled && portrait && w > h * 1.2;
}

// A map point as fractions of the map (0..1 each way) -> fractions of the
// arena's on-screen box. Turned clockwise: map x runs down the screen, map y
// runs from the right edge to the left.
export function toView(fx: number, fy: number, rotated: boolean): { x: number; y: number } {
  return rotated ? { x: 1 - fy, y: fx } : { x: fx, y: fy };
}

// CSS px per map unit, from the arena box's on-screen width. Turned, the box's
// width shows the map's height.
export function viewScale(boxWidth: number, map: any, rotated: boolean): number {
  return boxWidth / (rotated ? map.height : map.width);
}

// Draw something that must stand upright (an icon, a celebration) at (x, y) in
// layer pixels: turned about that point so it reads upright on screen. A plain
// call when the view isn't turned.
export function drawUpright(ctx: CanvasRenderingContext2D, x: number, y: number, rotated: boolean, draw: () => void): void {
  if (!rotated) {
    draw();
    return;
  }
  ctx.save();
  ctx.translate(x, y);
  ctx.rotate(-Math.PI / 2);
  ctx.translate(-x, -y);
  draw();
  ctx.restore();
}

// Is a round being played right now (past its countdown, not yet over)? When
// the arena fills a phone's screen, the HUD over it and the thumb prints fade
// away while it is (GameLayout provides it, from its getLive prop).
export const ArenaLiveContext = createContext<boolean>(false);

export function useArenaLive(): boolean {
  return useContext(ArenaLiveContext);
}
